import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { QuoteState } from '@nibtravel/shared/data-access/quote';
import {
  DatePickerType,
  IDatePickerConfig,
  MBSDateConfig,
  MBSRangeConfig,
} from '@nibtravel/shared/ui/forms/date-picker';
import { ValidationsHelper } from '@nibtravel/shared/ui/forms/validation';
import { Qqc } from '@nibtravel/shared/util/e2e';
import {
  DateUtil,
  IFormFieldValidation,
  ITravelDurationConfig,
  QuoteType,
} from '@nibtravel/shared/util/models';
import { add, format, isAfter, startOfTomorrow } from 'date-fns';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TravelDateSelector } from './qqc-travel-dates.selector';
import { DateValidators } from './validator/date.validator';
import { AlertType } from '@nibtravel/shared/ui/components/alert';

const departureDate = 'departureDate';
const coverEndDate = 'coverEndDate';
const waitingPeriodHours = 72;

@Component({
  selector: 'nibtravel-qqc-travel-dates',
  templateUrl: './qqc-travel-dates.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QqcTravelDatesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private departureValidations!: IFormFieldValidation[];
  private coverEndValidations!: IFormFieldValidation[];

  @Input() qqcForm!: FormGroup;
  @HostBinding('id') id = 'qqc-travel-dates-host';
  @HostBinding('class') hostClass = 'qqc-travel-dates';

  datePickerConfig!: IDatePickerConfig;
  quoteType!: QuoteType;
  e2eId = Qqc;
  AlertType = AlertType;

  quoteInput = this.store.selectSnapshot(QuoteState.quoteInput);

  isSetupReady$ = new BehaviorSubject<boolean>(false);
  durationConfig!: ITravelDurationConfig | undefined;
  waitingPeriodHours = waitingPeriodHours;

  startDatePlaceHolder = this.getCurrentTime();
  departureDatePlaceHolder = this.getDefaultDuration();

  get showWaitingAlert(): boolean {
    const control = this.qqcForm?.get(departureDate);

    // Show if departure date is sooner than the waiting period.
    if (control) {
      return (
        control.valid &&
        control.value &&
        !isAfter(new Date(Date.parse(control.value)), startOfTomorrow())
      );
    }
    return false;
  }

  constructor(private store: Store) {}

  ngOnInit(): void {
    if (this.qqcForm) {
      const quoteType = this.store.select(QuoteState.quoteType);
      const config = this.store.select(TravelDateSelector.durationConfigs);

      combineLatest([quoteType, config])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([quoteType, config]) => {
          if (config) {
            this.quoteType = quoteType;
            this.durationConfig = config;
            this.setupValidators();
            this.setupPickerConfig(quoteType);
            this.isSetupReady$.next(true);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private addControl(key: string): void {
    const initialValue = this.getInitialValue(key);
    const control = new FormControl(initialValue);
    this.qqcForm.addControl(key, control);
  }

  private getInitialValue(key: string): string | undefined {
    const fromState = this.quoteInput[key] as string;
    return fromState && fromState != ''
      ? format(new Date(fromState), DateUtil.DisplayFormat)
      : undefined;
  }

  private getDefaultDuration(): string {
    // default date is from config if undefined, then 7 is used
    const days = this.durationConfig?.defaultTravelDurationDays ?? 7;
    return format(add(new Date(), { days }), DateUtil.DisplayFormat);
  }

  private setupPickerConfig(quoteType: QuoteType): void {
    switch (quoteType) {
      case QuoteType.SingleTrip:
        this.datePickerConfig = this.singleTripPickerConfig();
        this.addControl(departureDate);
        this.addControl(coverEndDate);
        break;

      case QuoteType.MultiTrip:
        this.datePickerConfig = this.multiTripPickerConfig();
        this.addControl(departureDate);
        this.onTripSwitchCleanUp(coverEndDate);
        break;
    }
  }

  private singleTripPickerConfig(): IDatePickerConfig {
    const departure =
      this.getInitialValue(departureDate) || this.getCurrentTime();
    const coverEnd =
      this.getInitialValue(coverEndDate) || this.getDefaultDuration();
    const maxAdvancePurchaseDays =
      this.durationConfig?.maxAdvancePurchaseDays ?? 364;
    const maxTravelDurationDays =
      this.durationConfig?.maxTravelDurationDays ?? 366;

    return {
      type: DatePickerType.Range,
      settings: {
        ...MBSRangeConfig,
        defaultValue: [departure, coverEnd],
        min: new Date(),
        max: add(new Date(), {
          days: maxAdvancePurchaseDays + maxTravelDurationDays,
        }),
      },
      controlName: [departureDate, coverEndDate],
      validations: [this.departureValidations, this.coverEndValidations],
      validationLabel: { start: 'Departure date', end: 'Cover end date' },
    };
  }

  private getCurrentTime() {
    return format(Date.now(), DateUtil.DisplayFormat);
  }

  private multiTripPickerConfig(): IDatePickerConfig {
    const maxAdvancePurchaseDays = this.durationConfig?.maxAdvancePurchaseDays;
    return {
      type: DatePickerType.Date,
      settings: {
        ...MBSDateConfig,
        defaultValue: [this.getInitialValue(departureDate)],
        min: new Date(),
        max: add(new Date(), { days: maxAdvancePurchaseDays }),
      },
      controlName: [departureDate],
      validations: [...this.departureValidations],
      validationLabel: 'Departure date',
    };
  }

  private setupValidators(): void {
    this.departureValidations = [
      ValidationsHelper.Required,
      DateValidators.validDepartureDateValidation(),
    ];

    this.coverEndValidations = [ValidationsHelper.Required];
    if (this.durationConfig) {
      this.coverEndValidations.push(
        DateValidators.maxTripLengthValidation(
          departureDate,
          this.durationConfig
        )
      );
      this.departureValidations.push(
        DateValidators.maxAdvancePurchaseValidation(this.durationConfig)
      );
    }
  }

  /**
   * When user switches trip type, we need to clean up the control
   * Otherwise some of the validations will fail.
   *
   */
  private onTripSwitchCleanUp(controlName: string): void {
    const control = this.qqcForm.get(controlName);
    if (control) {
      control.setValue(null);
      this.qqcForm.removeControl(controlName);
    }
  }
}
